import { nanoid } from 'nanoid';
window.__moinrpc = {
  // map for holding the exposed functions
  exposedFunctions: new Map(),
  targets: new Map(),
};

export function expose(name, func) {
  if (window.__moinrpc.exposedFunctions.has(name)) {
    console.error('function with this name already has been exposed');
    return;
  }
  window.__moinrpc.exposedFunctions.set(name, func);
}

/**
 * 
  * @param {object} options
  * @param {Window} options.target
  * @param {string} [options.targetOrigin='*'] options.targetOrigin
  * @param {string} [options.service='moin-rpc'] options.service
 * @returns 
 */
export function getTarget(
  options = { target: null, targetOrigin: '*', service: 'moin-rpc' },
) {
  let target = options.target;
  if (!target) throw new Error('target must be set');
  // @ts-expect-error some weirdness with the types
  if (window.__moinrpc.targets.has(target.postMessage)) {
    // @ts-expect-error some weirdness with the types
    return window.__moinrpc.targets.get(target.postMessage);
  } else {
    const channel = createRPCChannel(options);
    // @ts-expect-error some weirdness with the types
    window.__moinrpc.targets.set(target.postMessage, channel);
    return channel;
  }
}
/**
 * 
  * @param {object} options
  * @param {Window} options.target
  * @param {string} [options.targetOrigin='*'] options.targetOrigin
  * @param {string} [options.service='moin-rpc'] options.service
 * @returns 
 */
const createRPCChannel = (
  options = { target: null, targetOrigin: '*', service: 'moin-rpc' },
) => {
  // map for holding the "reponse" promises
  const responseKeys = new Map();
  // we scoping the createChannel() function to the instance
  // otherwise serveral instances would interfer on the generic window message event
  const instanceId = nanoid();
  // target we want send messages to
  // this needs to be set before we can send messages
  // it also need to have a postMessage function
  let target = options.target;
  let targetOrigin = options.targetOrigin || '*';
  // identifier for the service that we don't use wrong postmessage events
  let service = options.service || 'moin-rpc';

  const createResponseMessage = (id, result, remoteInstanceId) => ({
    instanceId: remoteInstanceId,
    service,
    type: 'response',
    result,
    id,
  });

  const createErrorMessage = (id, error, remoteInstanceId) => ({
    instanceId: remoteInstanceId,
    service,
    type: 'error',
    result: error,
    id,
  });

  const createFunctionCallMessage = (func, args, id, instance) => ({
    instanceId: instance,
    service,
    type: 'call',
    func,
    id,
    args: JSON.stringify(args),
  });

  // Handler for the case we got a remote function call
  // if we have to function exposed we call the function and funnnel it through a promise resolve
  // in case the function is async, if not the result will go through the promise resolve
  // if the the function was not exposed we return an error via postMessage
  // we directly anser to the frame the message orginated from
  const handleFunctionCall = (event) => {
    const { func, args, id, instanceId } = event.data;
    const answerTarget = event.source;
    const funcToCall = window.__moinrpc.exposedFunctions.get(func);
    // function was not found so we return an error via postMessage
    if (!funcToCall) {
      answerTarget.postMessage(
        createErrorMessage(id, new Error('function not found'), instanceId),
        targetOrigin,
      );
      return;
    }
    // we call the function and return the result via postMessage
    const result = funcToCall.call(null, JSON.parse(args));
    Promise.resolve(result)
      .then((resultValue) => {
        answerTarget.postMessage(
          createResponseMessage(id, resultValue),
          targetOrigin,
        );
      })
      .catch((e) => {
        // error in the desired function call we pass on the error via postMessage
        answerTarget.postMessage(createErrorMessage(id, e), targetOrigin);
      });
  };

  // when the other frame answers to our call we resolve the promise in a success case
  // we also delete the promise from the map
  const handleResponse = (id, result) => {
    responseKeys.get(id).onSuccess(result);
    responseKeys.delete(id);
  };

  // when the function call produced an error or was not available we reject the promise and delete it from the map
  const handleError = (id, result) => {
    responseKeys.get(id).onError(result);
    responseKeys.delete(id);
  };

  // global handler for all messages
  const handleMessage = (event) => {
    // make sure we only consume message addressed to us
    if (!event.data) return;
    if (event.data.service !== service) return;
    const { type, id, result } = event.data;
    switch (type) {
      case 'call':
        handleFunctionCall(event);
        break;
      case 'response':
        handleResponse(id, result);
        break;
      case 'error':
        handleError(id, result);
        break;
      default:
        // this should never happen
        console.error('rpc unknown message type', event.data);
        break;
    }
  };
  window.addEventListener('message', handleMessage);

  return {
    call: (name, args = {}) => {
      // we might want opts here to override a target and targetOrigin

      // TODO check if args are valid json
      const readyState = target?.readyState || target?.document?.readyState;
      const isReady = readyState === 'complete' || readyState === 'interactive';
      return new Promise((resolve, reject) => {
        if (readyState === 'interactive') {
          console.warn('iframe is not fully loaded');
        }
        if (!isReady) reject(new Error(`target not ready`));
        if (!target) reject(new Error(`cant call ${name}, target set`));
        try {
          JSON.stringify(args);
        } catch {
          reject(new Error(`cant call ${name}, args not valid`));
        }
        // create handler for response and save with the corresponding id for handling the answer
        const onSuccess = (result) => resolve(result);
        const onError = (error) => reject(error);
        const id = nanoid();
        responseKeys.set(id, { name, onSuccess, onError });
        target.postMessage(
          createFunctionCallMessage(name, args, id, instanceId),
          targetOrigin,
        );
      });
    },

    /* remove: (name) => {
      exposedFunctions.delete(name);

      // TODO get open functions calls and error them
      // otherwise they will never resolve and are a potential memory leak
      // this break my brain atm, so I'll leave it for later me =) 
      // Have fun knobhead 
       for (let item of responseKeys.entires()) {
        console.log(item);
        if (item[1].name === name) {
          const openCall = responseKeys.get(item[0]);
          openCall.onError(new Error("function removed"));
          responseKeys.delete(item[0]);
        }
      } 
    },*/
    closeChannel: () => {
      responseKeys.clear();
      window.__moinrpc.exposedFunctions.clear();
      window.removeEventListener('message', handleMessage);
    },
  };
};
